import React, { useEffect, useMemo, useState } from 'react';
import { FaPlay, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { reportText as tr, reportLocale, reportAge, reportParticipantName } from '../utils/reportTranslation';
import type {
  CheckInData,
  ChildInfo,
  HealthHistoryItem,
  Symptom,
  VideoInfo,
} from '../types';
import { formatReportWallClock } from '../utils/reportDateTime';
import { resolveReportVideoUrl } from '../utils/videoUrl';

interface ReferenceReportProps {
  reportTitle?: string | null;
  childInfo: ChildInfo;
  checkIns: CheckInData[];
  videos: VideoInfo[];
  accessCode?: string;
  reportUrl: string;
  isStudyCoordinator?: boolean;
  onNavigateToECheck?: (index: number) => void;
}

interface EpisodePlacement {
  episode: number;
  eCheck: number;
  gapDays?: number;
}

interface SymptomRow {
  key: string;
  name: string;
  details: string[];
  trend?: string;
  value: string;
  tone: 'mild' | 'moderate' | 'severe' | 'neutral' | 'better' | 'unchanged' | 'worse' | 'new';
}

const isPresent = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0 && value.trim() !== '-1';
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const cleanWords = (value: string): string => value
  .replace(/_F[12]$/i, '')
  .replace(/^exp_/i, '')
  .replace(/^act_/i, '')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/_/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const symptomBase = (value: string): string => {
  const normalized = value.replace(/_F[12]$/i, '');
  if (/^Cough/i.test(normalized)) return 'Cough';
  if (/^NoisyBreathing/i.test(normalized)) return 'NoisyBreathing';
  if (/^Fever/i.test(normalized)) return 'Fever';
  if (/^Mucus/i.test(normalized)) return 'NasalCongestion';
  return normalized;
};

const symptomName = (value: string): string => {
  const labels: Record<string, string> = {
    ChestRetractions: 'Chest Retractions',
    NasalCongestion: 'Nasal Congestion',
    NoisyBreathing: 'Noisy Breathing',
    RapidBreathing: 'Rapid Breathing',
    RapidHeartRate: 'Rapid Heart Rate',
    SoreThroat: 'Sore Throat',
    VomitingDiarrhea: 'Vomiting/Diarrhea',
    SymptomsResolved: 'Symptoms Resolved',
    NoSymptoms: 'No Symptoms',
  };
  return tr(labels[value] ?? cleanWords(value));
};

const characteristicLabels: Record<string, string> = {
  CoughBarky: 'Barky',
  CoughDry: 'Dry',
  CoughWet: 'Wet',
  CoughPersistent: 'Persistent',
  CoughReflux: 'Reflux',
  CoughWhooping: 'Whooping',
  CoughOther: 'Other',
  NoisyBreathingInhale: 'Inhale',
  NoisyBreathingExhale: 'Exhale',
  NoisyBreathingBoth: 'Both',
  NoisyBreathingColorChange: 'Color change: blue or gray lips, or very pale',
  NoisyBreathingPauses: 'Pauses in breathing',
  NoisyBreathingSnoring: 'Snoring',
  NoisyBreathingGrunting: 'Grunting',
  NoisyBreathingFlaringNostrils: 'Flaring nostrils',
  NoisyBreathingHoarseVoice: 'Hoarse voice',
  MucusClearWhite: 'Clear or white',
  MucusYellowGreen: 'Yellow or green',
  MucusBrownRed: 'Brown or red-tinged',
};

const feverRoute = (type: string): string => {
  const normalized = type.replace(/_F[12]$/i, '');
  const routes: Record<string, string> = {
    FeverOrally: 'orally',
    FeverAxillary: 'axillary',
    FeverRectally: 'rectal',
    FeverEar: 'ear',
    FeverForehead: 'forehead',
  };
  return tr(routes[normalized] ?? '');
};

const formatSymptomRows = (
  symptoms: Symptom[] = [],
  isFollowUp = false,
  clinicFeverTemperature?: number,
): SymptomRow[] => {
  const groups = new Map<string, Symptom[]>();

  symptoms.forEach((symptom) => {
    const base = symptomBase(symptom.type ?? '');
    if (!base) return;
    const list = groups.get(base) ?? [];
    list.push(symptom);
    groups.set(base, list);
  });

  return Array.from(groups.entries()).map(([base, group]) => {
    const feverReading = base === 'Fever'
      ? group.find((item) => {
        const reading = Number(item.temperatureValue);
        return Number.isFinite(reading) && reading > 0;
      })
      : undefined;
    const primary = feverReading
      ?? group.find((item) => isPresent(item.symptomChange) || isPresent(item.improvement))
      ?? group.find((item) => isPresent(item.severity))
      ?? group[0];
    const details = group
      .map((item) => characteristicLabels[item.type.replace(/_F[12]$/i, '')])
      .filter((value): value is string => Boolean(value));
    const route = feverRoute(feverReading?.type ?? '');
    const temperature = feverReading
      ? `${feverReading.temperatureValue}°${feverReading.temperatureUnit || 'F'}${route ? ` ${route}` : ''}`
      : '';
    const currentTemperature = Number(feverReading?.temperatureValue);
    const temperatureDelta = base === 'Fever'
      && Number.isFinite(currentTemperature)
      && typeof clinicFeverTemperature === 'number'
      ? currentTemperature - clinicFeverTemperature
      : 0;
    const trend = Math.abs(temperatureDelta) >= 0.05
      ? tr('{{change}}°F since the clinic visit', { change: `${temperatureDelta > 0 ? '↑' : '↓'} ${Math.abs(temperatureDelta).toFixed(1)}` })
      : undefined;
    const legacyImprovement = primary.improvement?.trim() || '';
    const change = primary.symptomChange?.trim()
      || (/^\d+$/.test(legacyImprovement) ? '' : legacyImprovement);
    const severity = primary.severity?.trim() || '';
    const value = temperature || (isFollowUp && change ? change : severity) || 'Reported';
    const normalized = value.toLowerCase();
    const tone: SymptomRow['tone'] = normalized.includes('severe')
      ? 'severe'
      : normalized.includes('moderate')
        ? 'moderate'
        : normalized.includes('mild')
          ? 'mild'
          : normalized.includes('resolved') || normalized.includes('much better')
            ? 'better'
            : normalized.includes('little better')
              ? 'better'
              : normalized.includes('worse')
                ? 'worse'
                : normalized.includes('new')
                  ? 'new'
                  : normalized.includes('no change')
                    ? 'unchanged'
                    : 'neutral';

    return {
      key: base,
      name: symptomName(base),
      details: Array.from(new Set(details)).map((detail) => tr(detail)),
      trend,
      value: temperature || tr(cleanWords(value)),
      tone,
    };
  });
};

const feverTemperature = (checkIn?: CheckInData): number | undefined => {
  const fever = (checkIn?.symptoms ?? []).find((symptom) =>
    /^Fever/i.test(symptom.type ?? '')
    && Number.isFinite(Number(symptom.temperatureValue))
    && Number(symptom.temperatureValue) > 0);
  const value = Number(fever?.temperatureValue);
  return Number.isFinite(value) && value > 0 ? value : undefined;
};

const toDate = (value?: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (checkIn: CheckInData): string => {
  // A study date may contain only year/month; never expand it into an invented day/time.
  const maskedDate = checkIn.dateDisplayServer || checkIn.date;
  if (/^\d{4}-\d{2}$/.test(maskedDate)) return maskedDate;
  return formatReportWallClock(checkIn.date, 'en-US', {
    month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }) || checkIn.dateDisplayServer || tr('Date unavailable');
};

const formatBirthDate = (value?: string): string => {
  if (!value) return tr('Not provided');
  const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(reportLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatHistoryDate = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(reportLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
};

const buildEpisodePlacements = (checkIns: CheckInData[]): EpisodePlacement[] => {
  let episode = 1;
  let eCheck = 0;
  let previous: Date | null = null;

  return checkIns.map((checkIn) => {
    const current = toDate(checkIn.date);
    const gapDays = current && previous
      ? Math.floor((current.getTime() - previous.getTime()) / 86_400_000)
      : undefined;
    if (gapDays !== undefined && gapDays > 7) {
      episode += 1;
      eCheck = 0;
    }
    eCheck += 1;
    previous = current ?? previous;
    return { episode, eCheck, gapDays };
  });
};

const expandLegacyFollowUps = (checkIns: CheckInData[]): CheckInData[] => {
  if (checkIns.length !== 1) return checkIns;

  const source = checkIns[0];
  const suffixes = ['F1', 'F2'].filter((suffix) =>
    (source.symptoms ?? []).some((symptom) => new RegExp(`_${suffix}$`, 'i').test(symptom.type ?? '')));
  if (suffixes.length === 0) return checkIns;

  const initialSymptoms = (source.symptoms ?? []).filter((symptom) => !/_F[12]$/i.test(symptom.type ?? ''));
  const baseDate = toDate(source.date);
  const expanded: CheckInData[] = [{ ...source, symptoms: initialSymptoms }];

  suffixes.forEach((suffix, index) => {
    const followUpSymptoms = (source.symptoms ?? []).filter((symptom) =>
      new RegExp(`_${suffix}$`, 'i').test(symptom.type ?? ''));
    const overallChange = followUpSymptoms
      .map((symptom) => symptom.symptomChange || symptom.improvement || symptom.severity)
      .find((value) => isPresent(value) && !/^\d+$/.test(String(value)));
    const followUpDate = baseDate
      ? new Date(baseDate.getTime() + ((index + 1) * 15 * 60 * 1000)).toISOString()
      : source.date;

    expanded.push({
      ...source,
      id: `${source.id}-legacy-${suffix.toLowerCase()}`,
      date: followUpDate,
      dateDisplayServer: undefined,
      providerAskedForFollowUp: false,
      symptomImprovement: overallChange ? String(overallChange) : source.symptomImprovement,
      symptoms: followUpSymptoms,
    });
  });

  return expanded;
};

const historyText = (history: HealthHistoryItem[] = []): string => {
  const active = history.filter((item) => (
    !/^false$/i.test(item.value?.trim() ?? '')
    && !item.key?.toLowerCase().startsWith('meds_')
  ));
  if (active.length === 0) return tr('No health history reported.');

  const normalized = (item: HealthHistoryItem): string => [item.key, item.condition, item.value]
    .filter(isPresent)
    .join(' ')
    .toLowerCase();
  const consumed = new Set<HealthHistoryItem>();
  const take = (pattern: RegExp): HealthHistoryItem[] => {
    const matches = active.filter((item) => !consumed.has(item) && pattern.test(normalized(item)));
    matches.forEach((item) => consumed.add(item));
    return matches;
  };
  const sentences: string[] = [];

  if (take(/(?<!not )up to date|vaccinations? (?:are )?current|immunizations? (?:are )?current/).length > 0) {
    sentences.push(tr('Vaccinations are up to date.'));
  }

  const parentalAsthma = take(/parent(?:al)? (?:has |history|history of).*asthma/).length > 0;
  const suspectedAsthma = take(/suspected asthma|suspects? (?:the )?child (?:has|may have) asthma/).length > 0;
  if (suspectedAsthma) sentences.push(tr('Parent suspects the child has asthma.'));
  if (parentalAsthma) sentences.push(tr('Parent has history of asthma.'));

  const postTerm = take(/post[ -]?term|>\s*42 weeks?|greater than 42 weeks?/).length > 0;
  const smokeDuringPregnancy = take(/smoke.*pregnan|pregnan.*smoke/).length > 0;
  const currentSmoke = take(/currently exposed to smoke|current smoke exposure/).length > 0;
  if (postTerm) sentences.push(tr('Pregnancy term: >42 weeks.'));
  if (smokeDuringPregnancy && currentSmoke) {
    sentences.push(tr('Exposed to smoke during pregnancy and currently exposed to smoke.'));
  } else if (smokeDuringPregnancy) {
    sentences.push(tr('Exposed to smoke during pregnancy.'));
  } else if (currentSmoke) {
    sentences.push(tr('Currently exposed to smoke.'));
  }

  const priorConditions = [
    { label: 'bronchitis', found: take(/prior bronchitis/).length > 0 },
    { label: 'croup', found: take(/prior croup/).length > 0 },
    { label: 'pneumonia', found: take(/prior pneumonia/).length > 0 },
  ].filter((item) => item.found).map((item) => tr(item.label));
  if (priorConditions.length > 0) {
    const joined = priorConditions.length === 1
      ? priorConditions[0]
      : tr('{{items}} and {{last}}', { items: priorConditions.slice(0, -1).join(', '), last: priorConditions[priorConditions.length - 1] });
    sentences.push(tr('Prior {{conditions}}.', { conditions: joined }));
  }

  if (take(/prior hospitali[sz]ation.*respiratory|respiratory.*prior hospitali[sz]ation/).length > 0) {
    sentences.push(tr('Prior hospitalizations due to respiratory disease.'));
  }
  if (take(/\beczema\b/).length > 0) sentences.push(tr('Eczema.'));

  const allergies = [
    { label: 'peanuts', pattern: /peanut.*allerg|allerg.*peanut/ },
    { label: 'mold/mildew', pattern: /mold|mildew/ },
    { label: 'eggs', pattern: /egg.*allerg|allerg.*egg/ },
    { label: 'milk', pattern: /milk.*allerg|allerg.*milk/ },
    { label: 'seasonal (pollen)', pattern: /seasonal|pollen/ },
    { label: 'pets', pattern: /pet.*allerg|allerg.*pet/ },
  ].filter((allergy) => take(allergy.pattern).length > 0).map((allergy) => tr(allergy.label));
  // Consume the generic "Allergies" flag once its individual selections have been grouped.
  take(/^.*\ballergies\b.*$/);
  if (allergies.length > 0) sentences.push(tr('Allergies: {{allergies}}.', { allergies: allergies.join(', ') }));

  const wheezingFourOrMore = take(/wheez.*(?:4|four).*more/).length > 0;
  const wheezingWithoutColds = take(/wheez.*unrelated to colds?|wheez.*without colds?/).length > 0;
  if (wheezingFourOrMore && wheezingWithoutColds) {
    sentences.push(tr('Wheezing unrelated to colds, more than 4 times.'));
  } else if (wheezingWithoutColds) {
    sentences.push(tr('Wheezing unrelated to colds.'));
  } else if (wheezingFourOrMore) {
    sentences.push(tr('Wheezing more than 4 times.'));
  }

  active.filter((item) => !consumed.has(item)).forEach((item) => {
    const label = tr(cleanWords(item.condition));
    const value = item.value && !/^true$/i.test(item.value) ? `: ${reportAge(tr(item.value))}` : '';
    const details = [
      item.diagnosisDate ? tr('Recorded {{date}}', { date: formatHistoryDate(item.diagnosisDate) }) : '',
      item.notes?.trim() || '',
    ].filter(Boolean);
    sentences.push(`${label}${value}${details.length > 0 ? ` — ${details.join(' · ')}` : ''}`.replace(/[.\s]+$/g, '') + '.');
  });

  return sentences.join(' ');
};

const regularMedicationText = (history: HealthHistoryItem[] = []): string => {
  const values = history
    .filter((item) => item.key?.toLowerCase().startsWith('meds_') && item.value !== 'False')
    .map((item) => tr(cleanWords(item.condition)));
  return values.length > 0 ? values.join(', ') : tr('No regular medications reported.');
};

const exposureText = (checkIn: CheckInData): string => {
  const values = (checkIn.exposures ?? []).map((item) => [item.type, item.description]
    .filter(isPresent)
    .map((value) => tr(cleanWords(String(value))))
    .join(' - '));
  if (isPresent(checkIn.sickContacts)) values.push(tr(String(checkIn.sickContacts)));
  return values.filter(Boolean).join('. ') || tr('None reported.');
};

const hydrationText = (checkIn: CheckInData): string => {
  const values: string[] = [];
  if (isPresent(checkIn.drinkingStatus)) values.push(tr(String(checkIn.drinkingStatus)));
  if (isPresent(checkIn.diaperStatus)) {
    values.push(tr('Urination: {{value}}', { value: tr(String(checkIn.diaperStatus)) }));
  } else if (typeof checkIn.normalUrination === 'boolean') {
    values.push(tr('Urination: {{value}}', { value: tr(checkIn.normalUrination ? 'Normal' : 'Not normal') }));
  }
  if (isPresent(checkIn.poopStatus)) values.push(tr('Stools: {{value}}', { value: tr(String(checkIn.poopStatus)) }));
  if ((checkIn.dehydrationSigns?.length ?? 0) > 0) {
    values.push(tr('Dehydration signs: {{value}}', { value: (checkIn.dehydrationSigns ?? []).map((value) => tr(value)).join(', ') }));
  }
  return values.join('. ') || tr('Not provided.');
};

const sleepText = (checkIn: CheckInData): string => (checkIn.sleepQuality ?? [])
  .filter(isPresent)
  .map((value) => tr(value))
  .join(', ') || tr('Not provided.');

const activityText = (checkIn: CheckInData): string => {
  const values = (checkIn.activities ?? []).map((item) => [item.type, item.impact || item.description]
    .filter(isPresent)
    .map((value) => tr(String(value)))
    .join(' - '));
  return values.filter(Boolean).join('. ') || tr('Not provided.');
};

// Temporarily keep follow-up plans out of the shared viewer without removing the data or formatting logic.
const SHOW_FOLLOW_UP_PLAN = false;

const followUpPlanText = (checkIn: CheckInData): string => {
  const values: string[] = [];
  if (checkIn.followUpRequired) values.push(tr('Follow-up required'));
  if (checkIn.followUpDate) {
    const dueDate = new Date(checkIn.followUpDate);
    values.push(Number.isNaN(dueDate.getTime())
      ? tr('Due {{date}}', { date: checkIn.followUpDate })
      : tr('Due {{date}}', { date: dueDate.toLocaleString(reportLocale(), {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }) }));
  }
  if (isPresent(checkIn.followUpReason)) values.push(String(checkIn.followUpReason));
  return values.join(' · ');
};

const followUpMedicationText = (checkIn: CheckInData): string => [
  ...(checkIn.followUpMedications ?? []).map((value) => tr(value)),
  checkIn.followUpMedicationNotes,
].filter(isPresent).map(String).join(' · ');

const medicationStatusText = (checkIn: CheckInData): string => {
  if (isPresent(checkIn.medicationStatusLabel)) return tr(String(checkIn.medicationStatusLabel));
  if (checkIn.alreadyGivenMedication) {
    return tr('Medication already given — symptoms reflect post-medication state');
  }
  return tr('No medication given yet — symptoms reflect pre-medication state');
};

const videoForCheckIn = (videos: VideoInfo[], checkIn: CheckInData, index: number): VideoInfo[] => {
  const exact = videos.filter((video) => video.checkInId === checkIn.id);
  if (exact.length > 0) return exact;
  const phase = index === 0 ? 'initial' : `followup${index}`;
  return videos.filter((video) => video.phase === phase);
};

const videoDurationLabel = (duration?: string): string => {
  const value = duration?.trim();
  if (!value) return tr('DURATION NOT PROVIDED');
  const parts = value.split(':').map(Number);
  if (parts.length > 1 && parts.every(Number.isFinite)) {
    const seconds = parts.reduce((total, part) => (total * 60) + part, 0);
    return tr('{{seconds}} SEC', { seconds });
  }
  const numeric = Number(value.replace(/[^0-9.]/g, ''));
  return Number.isFinite(numeric) && numeric > 0 ? tr('{{seconds}} SEC', { seconds: Math.round(numeric) }) : value.toUpperCase();
};

const CompactVideoStrip: React.FC<{ videos: VideoInfo[]; sectionId?: string }> = ({ videos, sectionId }) => {
  const [activeVideo, setActiveVideo] = useState<VideoInfo | null>(null);
  const uniqueVideos = useMemo(() => {
    const seen = new Set<string>();
    return videos.filter((video) => {
      const key = video.videoUrl || video.id;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [videos]);

  useEffect(() => {
    if (!activeVideo) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveVideo(null);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [activeVideo]);

  return (
    <div id={sectionId} className="reference-video-strips">
      {uniqueVideos.map((video) => {
        const recordedAt = video.recordedAt
          ? formatReportWallClock(video.recordedAt, 'en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })
          : undefined;
        return (
          <div className="reference-video-strip" key={video.id || video.videoUrl}>
            <div className="reference-video-strip__details">
              <strong>Video · {videoDurationLabel(video.duration)}</strong>
              <span>{[tr(video.title || ''), recordedAt ? tr('Recorded {{date}}', { date: recordedAt }) : ''].filter(Boolean).join(' · ') || tr('Parent-submitted recording')}</span>
            </div>
            <button type="button" className="reference-video-watch" onClick={() => setActiveVideo(video)}>
              <FaPlay aria-hidden="true" />
              {tr("Watch Video")}
            </button>
          </div>
        );
      })}

      {activeVideo && (
        <div
          className="reference-video-modal"
          role="dialog"
          aria-modal="true"
          aria-label={tr('Playing {{title}}', { title: tr(activeVideo.title || 'eCheck video') })}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setActiveVideo(null);
          }}
        >
          <button
            type="button"
            className="reference-video-modal__close"
            onClick={() => setActiveVideo(null)}
            aria-label={tr("Close video")}
            title={tr("Close video")}
          >
            <FaTimes aria-hidden="true" />
          </button>
          <video
            className="reference-video-modal__player"
            src={resolveReportVideoUrl(activeVideo.videoUrl)}
            controls
            autoPlay
            playsInline
          >
            {tr("Your browser does not support secure video playback.")}
          </video>
        </div>
      )}
    </div>
  );
};

const ConcernScale: React.FC<{ value?: number; delta?: number }> = ({ value, delta }) => {
  const normalized = Math.max(0, Math.min(10, value ?? 0));
  return (
    <div className="reference-concern-scale">
      <div className="reference-concern-value">
        {value ?? '—'}<small>/10</small>
        {delta !== undefined && delta !== 0 && (
          <span className={`reference-delta ${delta < 0 ? 'reference-delta--down' : ''}`}>
            {delta > 0 ? '↑ +' : '↓ '}{delta}
          </span>
        )}
      </div>
      <div className="reference-concern-bar">
        <span style={{ left: `${normalized * 10}%` }} />
      </div>
    </div>
  );
};

const ContextRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="reference-context-row">
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

const SymptomList: React.FC<{
  checkIn: CheckInData;
  isFollowUp: boolean;
  clinicFeverTemperature?: number;
}> = ({ checkIn, isFollowUp, clinicFeverTemperature }) => {
  const rows = formatSymptomRows(checkIn.symptoms, isFollowUp, clinicFeverTemperature);
  if (rows.length === 0) {
    return <p className="reference-empty">{tr("No symptoms were reported for this eCheck.")}</p>;
  }

  return (
    <div>
      {rows.map((row) => (
        <div className="reference-symptom" key={row.key}>
          <div>
            <div className="reference-symptom-name">
              {row.name}
              {row.trend && <span className="reference-symptom-trend">{row.trend}</span>}
            </div>
            {row.details.length > 0 && (
              <div className="reference-subpills">
                {row.details.map((detail) => <span key={detail}>{detail}</span>)}
              </div>
            )}
          </div>
          <span className={`reference-chip reference-chip--${row.tone}`}>{row.value}</span>
        </div>
      ))}
    </div>
  );
};

const MamaBearLogo: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <div
    className={`reference-logo-lockup${compact ? ' reference-logo-lockup--compact' : ''}`}
    role="img"
    aria-label="MamaBear Health"
  >
    <img className="reference-logo-lockup__bear" src="/asset/mamabear_round.png" alt="" />
    {/* Previous report bear retained in case the PI asks to restore it.
    <img className="reference-logo-lockup__bear" src="/asset/mamabearicon.png" alt="" />
    */}
    <img className="reference-logo-lockup__wordmark" src="/asset/mamabear_black.png" alt="" />
  </div>
);

const RunningHeader: React.FC<{ childInfo: ChildInfo; isStudyCoordinator: boolean }> = ({ childInfo, isStudyCoordinator }) => (
  <div className="reference-running-head">
    <MamaBearLogo compact />
    <div className="reference-running-head__patient">
      <strong>{reportParticipantName(childInfo.name, isStudyCoordinator, childInfo.studyId)}</strong>
      {isStudyCoordinator && isPresent(childInfo.studyId || childInfo.clinicName) && <span>{childInfo.studyId || childInfo.clinicName}</span>}
      <span>{childInfo.dateOfBirth
        ? tr('Born {{date}}', { date: formatBirthDate(childInfo.dateOfBirth) })
        : reportAge(childInfo.age)}</span>
    </div>
  </div>
);

const SheetFooter: React.FC<{ current: number; total: number }> = ({ current, total }) => (
  <div className="reference-sheet-foot">{tr('{{current}} of {{total}}', { current, total })}</div>
);

const ReferenceReport: React.FC<ReferenceReportProps> = ({
  reportTitle,
  childInfo,
  checkIns,
  videos,
  accessCode,
  reportUrl,
  isStudyCoordinator = false,
  onNavigateToECheck,
}) => {
  useTranslation('report');
  const reportCheckIns = useMemo(() => expandLegacyFollowUps(checkIns), [checkIns]);
  const placements = useMemo(() => buildEpisodePlacements(reportCheckIns), [reportCheckIns]);
  const first = reportCheckIns[0];
  const final = reportCheckIns[reportCheckIns.length - 1] ?? first;
  const hasPreECheck = Boolean(first && (
    first.providerAskedForFollowUp
    || (first.childWasSeenIn && !/^no|not provided$/i.test(first.childWasSeenIn))
    || (first.diagnoses?.length ?? 0) > 0
    || (first.treatments?.length ?? 0) > 0
  ));
  const hasSingleEpisode = placements.length > 0 && placements.every((placement) => placement.episode === 1);
  const inlinePreECheck = hasSingleEpisode && hasPreECheck;
  const hasStandalonePreECheck = hasPreECheck && !inlinePreECheck;
  const totalSheets = 1 + (hasStandalonePreECheck ? 1 : 0) + reportCheckIns.length;
  const generated = final ? formatDateTime(final) : tr('Date unavailable');
  const displayAccessCode = accessCode?.trim() || tr('Validated for this session');
  const clinicFeverTemperature = feverTemperature(first);

  const navigateToECheck = (event: React.MouseEvent<HTMLAnchorElement>, index: number) => {
    event.preventDefault();
    const targetId = `echeck-${index + 1}`;
    window.history.replaceState(null, '', `#${targetId}`);
    if (onNavigateToECheck && index < checkIns.length) {
      onNavigateToECheck(index);
      return;
    }
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <main
      id="report-overview"
      className={`reference-document${isStudyCoordinator ? ' reference-document--study' : ''}`}
    >
      <section className="reference-sheet reference-summary-sheet">
        <div className="reference-masthead">
          <div className="reference-brand">
            <MamaBearLogo />
            <span>{isStudyCoordinator && reportTitle ? reportTitle : tr("Pediatric eCheck")}</span>
          </div>
          <div>
            <div className="reference-access-box">
              <span className="reference-label">{tr("Secure link")}</span>
              <small>
                {isStudyCoordinator
                  ? tr('To request a new link, contact the study administrator.')
                  : tr("To request a new link contact the child's parent.")}
              </small>
              <strong className="reference-link">{reportUrl}</strong>
              <span className="reference-label">{tr("Access code")}</span>
              <code>{displayAccessCode}</code>
            </div>
            <div className="reference-generated">{tr('Generated {{date}}', { date: generated })}</div>
          </div>
        </div>

        <div className="reference-patient">
          <div className="reference-patient-id">
            <div>
              <h1>{reportParticipantName(childInfo.name, isStudyCoordinator, childInfo.studyId)}</h1>
              <p>
                {reportAge(childInfo.age)} · {tr(cleanWords(childInfo.gender || 'Gender not provided'))}
                {childInfo.dateOfBirth ? ` · ${tr('Born {{date}}', { date: formatBirthDate(childInfo.dateOfBirth) })}` : ''}
              </p>
            </div>
            {(!isStudyCoordinator || isPresent(childInfo.studyId) || isPresent(childInfo.clinicName)) && <div>
              <span className="reference-label">{tr(isStudyCoordinator ? (childInfo.studyId ? 'Study ID' : 'Clinic') : 'Primary care')}</span>
              <strong>{(isStudyCoordinator
                ? [childInfo.studyId, childInfo.clinicName]
                : [childInfo.primaryCareProvider, childInfo.primaryCarePractice, childInfo.clinicName]
              ).find(isPresent) || tr('Not provided')}</strong>
              {!isStudyCoordinator && <p>{[
                isPresent(childInfo.primaryCareProvider) ? childInfo.primaryCarePractice : null,
                childInfo.primaryCarePhone,
              ].filter(isPresent).join(' · ')}</p>}
            </div>}
            <div>
              <span className="reference-label">{tr("Insurance")}</span>
              <p>{childInfo.insuranceStatus?.split(' - ').map((value) => tr(value)).join(' - ') || tr('Not provided')}</p>
            </div>
          </div>

          {inlinePreECheck && first && (
            <div className="reference-summary-followup">
              <div className="reference-summary-followup__head">
                <strong>{tr("Pre-eCheck information")}</strong>
                <span>{tr("Pre-eCheck")}</span>
              </div>
              <p>{tr("The clinic or care visit that prompted this eCheck, recorded before the first eCheck.")}</p>
              <dl className="reference-context">
                <ContextRow label={tr("Requested by")}>{first.followUpProviderName || childInfo.primaryCareProvider || tr('Not provided')}</ContextRow>
                <ContextRow label={tr("Seen at")}>{tr(first.childWasSeenIn || first.seenByProviderLocation || 'Not provided')}</ContextRow>
                {(first.diagnoses?.length ?? 0) > 0 && <ContextRow label={tr("Diagnosis")}>{first.diagnoses?.map((value) => tr(value)).join(', ')}</ContextRow>}
                {(first.treatments?.length ?? 0) > 0 && <ContextRow label={tr("Treatment")}>{first.treatments?.map((value) => tr(value)).join(', ')}</ContextRow>}
                {SHOW_FOLLOW_UP_PLAN && followUpPlanText(first) && <ContextRow label={tr("Follow-up plan")}>{followUpPlanText(first)}</ContextRow>}
                {followUpMedicationText(first) && <ContextRow label={tr("Follow-up meds")}>{followUpMedicationText(first)}</ContextRow>}
              </dl>
              <div className="reference-subheading">{tr("Symptoms at the clinic visit")}</div>
              <SymptomList checkIn={first} isFollowUp={false} />
            </div>
          )}
          <div className="reference-episode-panels">
            {reportCheckIns.map((checkIn, index) => {
              const placement = placements[index];
              const previous = reportCheckIns[index - 1];
              const symptoms = formatSymptomRows(checkIn.symptoms, index > 0, clinicFeverTemperature).map((item) => item.name);
              const concernDelta = previous?.levelOfConcern !== undefined && checkIn.levelOfConcern !== undefined
                ? checkIn.levelOfConcern - previous.levelOfConcern
                : undefined;
              const followUpPlan = followUpPlanText(checkIn);
              return (
                <a
                  className="reference-episode-panel"
                  href={`#echeck-${index + 1}`}
                  key={checkIn.id}
                  onClick={(event) => navigateToECheck(event, index)}
                >
                  <span className={`reference-episode-kind${placement.eCheck > 1 ? ' reference-episode-kind--followup' : ''}`}>
                    {tr(placement.eCheck > 1 ? 'Follow-up check-in data' : 'First MamaBear eCheck')}
                  </span>
                  <div className="reference-episode-head">
                    <strong>{tr('Episode {{episode}} · eCheck {{check}}', { episode: placement.episode, check: placement.eCheck })}</strong>
                    <span>{formatDateTime(checkIn)}</span>
                  </div>
                  {placement.eCheck === 1 && checkIn.providerAskedForFollowUp && (
                    <small>{tr('Requested by {{provider}}', { provider: checkIn.followUpProviderName || childInfo.primaryCareProvider || tr('the child’s provider') })}</small>
                  )}
                  {!(placement.eCheck === 1 && checkIn.providerAskedForFollowUp) && (
                    <p>{symptoms.join(' · ') || tr('No symptom details reported')}</p>
                  )}
                  {placement.eCheck === 1 && isPresent(checkIn.parentConcernText) && (
                    <p className="reference-episode-primary-concern">
                      <strong>{tr("Primary concern:")}</strong> {checkIn.parentConcernText}
                    </p>
                  )}
                  <div className="reference-episode-concern">
                    <span>{tr("Parent's Concern Today")}</span>
                    <strong>
                      {checkIn.levelOfConcern ?? '—'}<small>/10</small>
                      {placement.eCheck > 1 && concernDelta !== undefined && concernDelta !== 0 && (
                        <small> ({concernDelta > 0 ? '+' : ''}{concernDelta})</small>
                      )}
                    </strong>
                    <div><i style={{ left: `${Math.max(0, Math.min(10, checkIn.levelOfConcern ?? 0)) * 10}%` }} /></div>
                  </div>
                  <div className="reference-episode-meta">
                    {isPresent(checkIn.symptomDuration) && <span>{tr('Duration: {{duration}}', { duration: reportAge(tr(checkIn.symptomDuration || '')) })}</span>}
                    {placement.eCheck > 1 && isPresent(checkIn.symptomImprovement) && <span>{tr('Overall change: {{change}}', { change: tr(checkIn.symptomImprovement || '') })}</span>}
                    {SHOW_FOLLOW_UP_PLAN && followUpPlan && <span>{followUpPlan}</span>}
                  </div>
                  <span className="reference-episode-open">{tr("Open full eCheck: symptoms, parent-reported context and follow-up data ↓")}</span>
                </a>
              );
            })}
          </div>
          {videos.length > 0 && (
            <div className="reference-web-note">
              <strong>{tr("Videos are available in this secure web report.")}</strong>
              {tr('Open an eCheck below to review its parent-submitted recording alongside the clinical context.')}
            </div>
          )}
        </div>
        <SheetFooter current={1} total={totalSheets} />
      </section>

      {hasStandalonePreECheck && first && (
        <section id="pre-echeck" className="reference-sheet">
          <RunningHeader childInfo={childInfo} isStudyCoordinator={isStudyCoordinator} />
          <article className="reference-card">
            <div className="reference-card-head">
              <h2>{tr("Pre-eCheck context")}</h2>
              <span className="reference-badge reference-badge--initial">{tr("Pre-eCheck")}</span>
            </div>
            <p className="reference-card-sub">{tr("Why this report was requested - recorded before the first eCheck")}</p>
            <dl className="reference-context">
              <ContextRow label={tr("Requested by")}>{first.followUpProviderName || childInfo.primaryCareProvider || tr('Not provided')}</ContextRow>
              <ContextRow label={tr("Seen at")}>{tr(first.childWasSeenIn || first.seenByProviderLocation || 'Not provided')}</ContextRow>
              {(first.diagnoses?.length ?? 0) > 0 && <ContextRow label={tr("Diagnosis")}>{first.diagnoses?.map((value) => tr(value)).join(', ')}</ContextRow>}
              {(first.treatments?.length ?? 0) > 0 && <ContextRow label={tr("Treatment")}>{first.treatments?.map((value) => tr(value)).join(', ')}</ContextRow>}
            </dl>
            <div className="reference-subheading">{tr("Symptoms at that visit")}</div>
            <SymptomList checkIn={first} isFollowUp={false} />
          </article>
          <SheetFooter current={2} total={totalSheets} />
        </section>
      )}

      {reportCheckIns.map((checkIn, index) => {
        const placement = placements[index];
        const previous = reportCheckIns[index - 1];
        const eCheckVideos = videoForCheckIn(videos, checkIn, index);
        const concernDelta = previous?.levelOfConcern !== undefined && checkIn.levelOfConcern !== undefined
          ? checkIn.levelOfConcern - previous.levelOfConcern
          : undefined;
        const sheetNumber = 2 + (hasStandalonePreECheck ? 1 : 0) + index;

        return (
          <section id={`echeck-${index + 1}`} className="reference-sheet" key={checkIn.id}>
            <RunningHeader childInfo={childInfo} isStudyCoordinator={isStudyCoordinator} />
            {placement.eCheck === 1 && placement.episode > 1 && (
              <div className="reference-episode-break"><span>{tr('New episode')}{placement.gapDays ? ` · ${tr('{{count}} days later', { count: placement.gapDays })}` : ''}</span></div>
            )}
            <article className="reference-card">
              <div className="reference-card-head">
                <h2>eCheck · {formatDateTime(checkIn)}</h2>
                <span className={`reference-badge ${placement.eCheck > 1 ? 'reference-badge--followup' : placement.episode > 1 ? 'reference-badge--episode' : 'reference-badge--initial'}`}>
                  {tr('Episode {{episode}} · eCheck {{check}}', { episode: placement.episode, check: placement.eCheck })}
                </span>
              </div>
              <p className="reference-card-sub">{medicationStatusText(checkIn)}</p>

              <div className="reference-concern">
                <div className="reference-label">{tr("Parent's Concern Today")}</div>
                <ConcernScale value={checkIn.levelOfConcern} delta={placement.eCheck > 1 ? concernDelta : undefined} />
                {checkIn.parentConcernText && (
                  <blockquote><strong>{tr("Parent's Reported Concern:")}</strong> “{checkIn.parentConcernText}”</blockquote>
                )}
              </div>

              <p className="reference-card-sub reference-card-sub--spaced">
                {tr('Duration of illness: {{duration}}', { duration: reportAge(tr(checkIn.symptomDuration || 'Not provided')) })}{!!checkIn.symptomHistoryLabels?.length && <> · {tr('Symptom history')}: {checkIn.symptomHistoryLabels.map(label => tr(label)).join(', ')}</>}{!checkIn.symptomHistoryLabels?.length && <> · {tr(checkIn.isRepetitive === 'Yes' ? 'Parent reports symptoms have been experienced in the past' : 'Parent reports symptoms have not been experienced in the past')}</>}
              </p>
              <div className="reference-subheading">
                {placement.eCheck > 1
                  ? tr('Symptom trajectory since eCheck {{check}}', { check: placement.eCheck - 1 })
                  : checkIn.providerAskedForFollowUp
                    ? tr('Parent Reported Symptom Update')
                    : tr('Parent reported symptoms')}
              </div>
              <SymptomList
                checkIn={checkIn}
                isFollowUp={placement.eCheck > 1}
                clinicFeverTemperature={clinicFeverTemperature}
              />

              <div className="reference-subheading">{tr("Context")}</div>
              <dl className="reference-context">
                <ContextRow label={tr("Current meds")}>{regularMedicationText(childInfo.healthHistory)}</ContextRow>
                <ContextRow label={tr("Exposures")}>{exposureText(checkIn)}</ContextRow>
                <ContextRow label={tr("Hydration")}>{hydrationText(checkIn)}</ContextRow>
                <ContextRow label={tr("Sleep")}>{sleepText(checkIn)}</ContextRow>
                <ContextRow label={tr("Activity at symptom onset")}>{activityText(checkIn)}</ContextRow>
                <ContextRow label={tr("Activity Level")}>{tr(checkIn.activityLevel || 'Not provided.')}</ContextRow>
                {(checkIn.medications?.length ?? 0) > 0 && (
                  <ContextRow label={tr("Medication given")}>
                    {checkIn.medications.map((item) => [item.name, item.dosage, item.time].filter(isPresent).join(' · ')).join(', ')}
                  </ContextRow>
                )}
                {SHOW_FOLLOW_UP_PLAN && followUpPlanText(checkIn) && <ContextRow label={tr("Follow-up plan")}>{followUpPlanText(checkIn)}</ContextRow>}
                {followUpMedicationText(checkIn) && <ContextRow label={tr("Follow-up meds")}>{followUpMedicationText(checkIn)}</ContextRow>}
                <ContextRow label={tr("Health history")}>{historyText(childInfo.healthHistory)}</ContextRow>
                {isPresent(checkIn.additionalComments) && <ContextRow label={tr("Additional comments")}>{checkIn.additionalComments}</ContextRow>}
              </dl>

              {eCheckVideos.length > 0 ? (
                <div className="reference-video" id={index === 0 ? 'report-videos' : undefined}>
                  <CompactVideoStrip videos={eCheckVideos} sectionId={`echeck-video-${index + 1}`} />
                </div>
              ) : (
                <div className="reference-video-empty">{tr("No recording is attached to this eCheck.")}</div>
              )}
            </article>
            {index === reportCheckIns.length - 1 && (
              <div className="reference-report-footer">
                <strong>MamaBear Health</strong> - {tr("helping parents tell a child's story clearly.")}<br />
                {tr("This report was generated by the child's parent through the MamaBear app. It reflects the parent's observations and is not a substitute for clinical assessment.")}
              </div>
            )}
            <SheetFooter current={sheetNumber} total={totalSheets} />
          </section>
        );
      })}
    </main>
  );
};

export default ReferenceReport;
